export const WORKFLOW_TEMPLATE_CATEGORIES = [
  { id: "finance", label: "Finance" },
  { id: "hr", label: "HR" },
  { id: "support", label: "Support" },
  { id: "sales", label: "Sales" },
  { id: "operations", label: "Operations" },
  { id: "legal", label: "Legal" },
  { id: "it", label: "IT" },
];

export const WORKFLOW_TEMPLATE_CATEGORY_IDS = WORKFLOW_TEMPLATE_CATEGORIES.map(
  (category) => category.id
);

export const WORKFLOW_TEMPLATE_PROMPTS = [
  {
    id: "invoice-approval",
    title: "Invoice approval",
    shortDescription:
      "Extract invoice details, route approvals by amount, and update finance records.",
    category: "finance",
    tags: ["invoice", "approval", "finance", "accounts payable"],
    featured: true,
    integrations: ["Forms", "Email", "Database", "AI agent"],
    prompt: `Please first produce a concise plan before building anything.

Build an invoice approval workflow. The workflow should support a trigger form for uploaded invoices and an email trigger for invoices sent by vendors. Required data includes vendor name, invoice number, amount, currency, due date, requester, line items, and the original invoice file.

Core steps:
1. Use an AI agent to extract invoice fields and flag missing or suspicious values.
2. Store each invoice in a database table with status, requester, amount, approver, due date, and extraction confidence.
3. Use a gateway to route invoices by amount: low value goes to finance review, medium value needs manager approval, and high value needs finance plus executive approval.
4. Create dynamic review tasks for the required approvers.
5. Use a merge to converge approval threads before final processing.
6. Send email updates to the requester and finance team.
7. Update the database table with approved, rejected, or needs-info status.

Human review points: extracted data validation, approval decision, rejection reason, and exception handling.

Outputs: an updated invoice record, approval audit trail, requester notification, finance notification, and a summary document for high-value invoices.

Assumptions: if exact amount thresholds are not provided, propose sensible defaults and ask me to confirm them before building.`,
  },
  {
    id: "employee-onboarding",
    title: "Employee onboarding",
    shortDescription:
      "Run a guided multi-step new-hire intake, fan out setup tasks, and track follow-up.",
    category: "hr",
    tags: ["onboarding", "hr", "new hire", "multi-step flow"],
    featured: true,
    integrations: [
      "Forms",
      "Multi-step flow",
      "Email",
      "Database",
      "Document editor",
    ],
    prompt: `Please first produce a concise plan before building anything.

Build an employee onboarding workflow that begins with a multi-step flow. Step 1 captures core new-hire details (full name, role, department, manager, start date, personal email). Step 2 captures work setup needs (location type: remote, hybrid, or office; equipment; system access). Use conditional fields so equipment and shipping address only appear for remote and hybrid hires.

Core steps:
1. Build the intake as a multi-step flow with a stepper: the first form is the flow entry and the second form is a flow member.
2. Create a new employee onboarding row in a database table.
3. Use an AI agent to summarize onboarding requirements and identify missing information.
4. Use a gateway to route remote, hybrid, and office-based hires through the right setup steps.
5. Start parallel dynamic review tasks for IT, facilities, and the hiring manager.
6. Use a merge with function ALL to wait for the IT, facilities, and manager tasks to complete.
7. Send a welcome email to the new hire with first-day instructions.
8. Add a 30-day time delay, then send a follow-up task to the manager.
9. Use the document editor to create a final onboarding summary document.

Human review points: manager confirmation, IT access approval, facilities readiness, and 30-day check-in notes.

Outputs: onboarding database status, task completion audit trail, welcome email, manager check-in reminder, and final summary document.

Assumptions: if the company has no fixed onboarding SLA, propose default deadlines for IT, facilities, and manager tasks before building.`,
  },
  {
    id: "support-ticket-triage",
    title: "Support ticket triage",
    shortDescription:
      "Classify customer issues, escalate urgent tickets, and summarize support trends.",
    category: "support",
    tags: ["support", "ticket", "triage", "customer"],
    featured: true,
    integrations: ["Forms", "Email", "Database", "AI agent"],
    prompt: `Please first produce a concise plan before building anything.

Build a support ticket triage workflow. The workflow should start from a trigger form or support email trigger that captures customer name, customer tier, account identifier, issue description, attachments, and requested urgency.

Core steps:
1. Use an AI agent to classify category, urgency, sentiment, and likely next action.
2. Create or update a ticket record in a database table.
3. Use a gateway to route VIP customers, critical incidents, and negative-sentiment tickets to an escalation path.
4. Create a dynamic review task for the assigned support owner, and a separate escalation task for urgent tickets.
5. Send status emails to the customer when a ticket is created, escalated, and resolved.
6. On a weekly schedule trigger, query the ticket table and use an AI agent to summarize ticket volume, categories, and trends.

Human review points: assigned owner, escalation decision, customer response quality, and resolution confirmation.

Outputs: ticket database record, customer emails, an internal escalation task, and a weekly trend summary document.

Assumptions: if customer tiers or severity levels are not defined, propose a simple routing model before building.`,
  },
  {
    id: "sales-lead-qualification",
    title: "Sales lead qualification",
    shortDescription:
      "Enrich and score inbound leads, create sales tasks, and send tailored follow-up.",
    category: "sales",
    tags: ["sales", "lead", "qualification", "enrichment"],
    featured: true,
    integrations: ["Forms", "AI agent", "Email", "Database"],
    prompt: `Please first produce a concise plan before building anything.

Build a sales lead qualification workflow. The workflow should start from a website trigger form that captures name, company, email, phone, role, company size, region, use case, budget range, timeline, and freeform notes.

Core steps:
1. Use an AI agent with web search enabled to enrich the lead with public company information (industry, size, recent news).
2. Use an AI agent to score fit, urgency, potential value, and the next-best action.
3. Create a CRM-style lead record in a database table.
4. Use a gateway to route qualified leads to a sales dynamic review task and lower-score leads to nurture.
5. Send a personalized follow-up email based on the lead's use case and urgency.
6. Add a scheduled reminder or time delay for nurture follow-up.
7. Update lead status after owner review.

Human review points: score override, sales owner assignment, qualification decision, and follow-up approval.

Outputs: enriched lead database record, personalized email, sales task, and a nurture reminder for lower-score leads.

Assumptions: if qualification criteria are not defined, propose a simple fit and urgency scoring rubric before building.`,
  },
  {
    id: "api-data-sync",
    title: "API data sync",
    shortDescription:
      "Receive webhook events, transform the payload with code, and push to an external system.",
    category: "it",
    tags: ["api", "webhook", "integration", "sync"],
    featured: true,
    integrations: ["Webhook", "Code", "HTTP/API", "Database"],
    prompt: `Please first produce a concise plan before building anything.

Build an integration workflow that keeps an external system in sync. The workflow should start from a webhook trigger that receives a JSON event (for example a new or updated record from another application).

Core steps:
1. Use a webhook trigger to receive the incoming JSON payload.
2. Use a code component to parse the payload, validate required fields, and transform it into the shape the destination API expects.
3. Use a gateway to branch on event type (created, updated, deleted) and on validation result.
4. Use an HTTP request to call the destination API for valid events.
5. Use a code component to parse the API response and detect success or failure.
6. Store the sync result (source id, destination id, status, error) in a database table for auditing.
7. On failure, send an email alert with the payload and error so someone can investigate.

Human review points: an optional manual review task for events that fail validation or repeatedly fail the destination API.

Outputs: a synced record in the destination system, a database audit row per event, and failure alerts.

Assumptions: if the destination API, authentication, and field mapping are not specified, ask me for the API docs and propose a default field mapping before building. Use web search to look up the API shape if I give you a service name.`,
  },
  {
    id: "scheduled-ops-report",
    title: "Scheduled operations report",
    shortDescription:
      "On a schedule, query your data, summarize it with AI, and email a generated report.",
    category: "operations",
    tags: ["schedule", "report", "analytics", "digest"],
    featured: true,
    integrations: ["Schedule", "Database", "File export", "Email"],
    prompt: `Please first produce a concise plan before building anything.

Build a scheduled reporting workflow. The workflow should start from a schedule trigger (for example every Monday morning) and produce a report from data already stored in a database table.

Core steps:
1. Use a schedule trigger to start the run on a recurring cadence.
2. Query a database table to pull the rows for the reporting period, using aggregates (counts, sums, averages) where useful.
3. Use a code component to compute period-over-period changes and format the numbers.
4. Use an AI agent to write a short narrative summary highlighting trends, outliers, and recommended actions.
5. Use a create file step to generate a PDF or XLSX report combining the metrics and the narrative.
6. Email the report to the distribution list as an attachment or link.

Human review points: an optional review task to approve the report before it is sent to a wider audience.

Outputs: a generated report file, an emailed digest, and an optional saved record of each report run.

Assumptions: if the source table, metrics, cadence, and recipients are not defined, propose sensible defaults and ask me to confirm them before building.`,
  },
  {
    id: "contract-review",
    title: "Contract review",
    shortDescription:
      "Extract contract risks, route legal approvals, and track renewal obligations.",
    category: "legal",
    tags: ["contract", "legal", "risk", "approval"],
    featured: false,
    integrations: ["Email", "Forms", "Database", "Document editor"],
    prompt: `Please first produce a concise plan before building anything.

Build a contract review workflow. The workflow should start from an email trigger or trigger form where a requester submits a contract file, counterparty, contract type, estimated value, requested deadline, and business owner.

Core steps:
1. Use an AI agent to extract parties, contract value, effective date, renewal date, termination notice period, governing law, risky clauses, and missing sections.
2. Store the contract review record in a database table.
3. Use a gateway to route high-risk or high-value contracts to legal approval and standard contracts to business-owner review.
4. Create dynamic review tasks for legal, finance, and the business owner when required.
5. Use a merge to converge approval threads before the final decision.
6. Send a final email with approval, rejection, or required edits.
7. Update contract status and renewal reminder fields in the database.

Human review points: risk assessment, clause approval, final decision, and required edits.

Outputs: contract database record, approval audit trail, final email, and a document editor summary of key terms and risks.

Assumptions: if risk thresholds are not defined, propose defaults for value, renewal length, and risky clause categories before building.`,
  },
  {
    id: "vendor-onboarding",
    title: "Vendor onboarding",
    shortDescription:
      "Collect vendor details, assess risk, approve procurement, and send onboarding emails.",
    category: "operations",
    tags: ["vendor", "procurement", "onboarding", "risk"],
    featured: false,
    integrations: ["Forms", "Email", "Database", "Document editor"],
    prompt: `Please first produce a concise plan before building anything.

Build a vendor onboarding workflow. The workflow should start from a trigger form that captures vendor company name, contact details, tax information, services provided, certifications, insurance documents, regions served, data access needs, and requester.

Core steps:
1. Use an AI agent to validate completeness and identify risk indicators.
2. Store the vendor record in a database table.
3. Use a gateway to route high-risk vendors for extra legal or security review.
4. Create a dynamic review task for procurement approval.
5. Create additional dynamic review tasks for legal, security, or finance when required.
6. Use a merge to converge approvals before final onboarding.
7. Send a welcome email after approval and update vendor status.
8. Use the document editor to create a vendor onboarding summary.

Human review points: procurement approval, risk override, legal/security review, and final onboarding decision.

Outputs: vendor database record, approval audit trail, welcome email, and vendor summary document.

Assumptions: if high-risk criteria are not defined, propose defaults for data access, region, contract value, and missing certifications before building.`,
  },
  {
    id: "project-intake-and-approval",
    title: "Project intake and approval",
    shortDescription:
      "Summarize project requests, route approvals, and notify teams of decisions.",
    category: "operations",
    tags: ["project", "intake", "approval", "budget"],
    featured: false,
    integrations: ["Forms", "Email", "Database", "AI agent"],
    prompt: `Please first produce a concise plan before building anything.

Build a project intake and approval workflow. The workflow should start from a trigger form that captures requester, project name, business goal, budget, timeline, required resources, expected impact, risks, and strategic priority.

Core steps:
1. Use an AI agent to summarize scope, business value, resource needs, and risks.
2. Store the project request in a database table.
3. Use a gateway to route requests by budget, risk, and strategic priority.
4. Create dynamic review tasks for the required approvers.
5. Use a merge to converge approvals before the final decision.
6. Send an email with the approval, rejection, or request-for-more-information decision.
7. Send a kickoff email with next steps for approved projects.
8. Update the request status in the database.

Human review points: budget approval, strategic priority review, risk assessment, and final decision.

Outputs: project request database record, approval audit trail, decision email, kickoff email, and summary document.

Assumptions: if approval thresholds are not defined, propose default budget and priority rules before building.`,
  },
  {
    id: "marketing-campaign-approval",
    title: "Marketing campaign approval",
    shortDescription:
      "Review campaign briefs, coordinate approvals, and update launch readiness.",
    category: "sales",
    tags: ["marketing", "campaign", "approval", "brand"],
    featured: false,
    integrations: ["Forms", "Email", "Database", "Document editor"],
    prompt: `Please first produce a concise plan before building anything.

Build a marketing campaign approval workflow. The workflow should start from a trigger form that captures campaign name, owner, launch date, channel, budget, brief, target audience, assets, claims, and required reviewers.

Core steps:
1. Use an AI agent to check the brief and assets for brand, policy, and compliance concerns.
2. Store the campaign request in a database table.
3. Use a gateway to route by channel, budget, claims, and risk level.
4. Create parallel dynamic review tasks for legal, brand, and budget owners.
5. Use a merge to converge approval threads before the final decision.
6. Send a final launch-approval or revision-request email.
7. Send a launch-readiness email to the campaign owner and stakeholders when a campaign is approved.
8. Update the campaign status in the database.

Human review points: legal approval, brand approval, budget approval, and final launch decision.

Outputs: campaign database record, approval audit trail, decision email, launch-readiness notification, and campaign summary document.

Assumptions: if review requirements differ by channel, propose a default routing matrix before building.`,
  },
  {
    id: "customer-feedback-analysis",
    title: "Customer feedback analysis",
    shortDescription:
      "Analyze feedback sentiment, escalate urgent issues, and summarize customer themes.",
    category: "support",
    tags: ["feedback", "sentiment", "customer success", "analysis"],
    featured: false,
    integrations: ["Email", "Forms", "Database", "AI agent"],
    prompt: `Please first produce a concise plan before building anything.

Build a customer feedback analysis workflow. The workflow should start from an email trigger, API-triggered submission, or trigger form that captures customer identity, feedback text, source, product area, account owner, and attachments.

Core steps:
1. Use an AI agent to extract sentiment, themes, urgency, feature requests, and churn risk.
2. Store feedback in a database table for trend analysis.
3. Use a gateway to escalate negative, urgent, or churn-risk feedback.
4. Create a dynamic review task for customer success follow-up on escalated feedback.
5. Send acknowledgement emails to the customer when appropriate.
6. On a monthly schedule trigger, query the feedback table and summarize top themes and trend changes.

Human review points: escalation decision, customer success response, theme validation, and monthly insights approval.

Outputs: feedback database record, follow-up task, customer email, and a monthly insights document.

Assumptions: if sentiment escalation criteria are not defined, propose default thresholds before building.`,
  },
  {
    id: "compliance-training-tracker",
    title: "Compliance training tracker",
    shortDescription:
      "Check training completion, send reminders, capture proof, and report compliance.",
    category: "operations",
    tags: ["compliance", "training", "reminders", "reporting"],
    featured: false,
    integrations: ["Schedule", "Email", "Forms", "Database"],
    prompt: `Please first produce a concise plan before building anything.

Build a compliance training tracker workflow. The workflow should start from a schedule trigger that checks a training database table for users who are incomplete, overdue, or nearing a deadline.

Core steps:
1. Loop through the list of users with incomplete training, creating one thread per user.
2. Use a gateway to route users by due-soon, overdue, and complete status.
3. Send reminder emails to incomplete users and escalation emails for overdue users.
4. Use a trigger form to capture proof of completion or exemption requests.
5. Update each user's compliance status in the database table.
6. Add a time delay for follow-up reminders when no proof is received.
7. Use the document editor to create a compliance report for managers.

Human review points: exemption approval, overdue escalation, and final compliance report review.

Outputs: updated compliance database rows, reminder emails, escalation emails, proof records, and a compliance summary document.

Assumptions: if training deadlines are not defined, propose default reminder timing and escalation rules before building.`,
  },
  {
    id: "document-generation",
    title: "Document generation",
    shortDescription:
      "Collect details in a form and generate a branded PDF or DOCX from a template.",
    category: "legal",
    tags: ["documents", "pdf", "docx", "generation"],
    featured: false,
    integrations: ["Forms", "Document editor", "File export", "Email"],
    prompt: `Please first produce a concise plan before building anything.

Build a document generation workflow (for example an offer letter, NDA, statement of work, or certificate). The workflow should start from a trigger form that captures every field the document needs (recipient name, dates, amounts, terms, and any custom clauses).

Core steps:
1. Use a trigger form to collect the document inputs, with conditional fields for optional clauses.
2. Use a code or AI agent component to validate and format the inputs (dates, currency, capitalized names).
3. Optionally store the document request in a database table.
4. Use a create file step to generate a PDF or DOCX by merging the inputs into a template (uploaded template or rich text editor).
5. Create a dynamic review task so an approver can review the draft document before it goes out.
6. After approval, email the final document to the recipient and the requester.

Human review points: input validation, draft document approval, and final send confirmation.

Outputs: a generated document file, an approval record, and an email delivering the final document.

Assumptions: if the document type and template are not provided, propose a default layout and ask me to confirm the fields and template before building.`,
  },
  {
    id: "application-intake-flow",
    title: "Application intake flow",
    shortDescription:
      "Guide applicants through a multi-step form, then screen and route submissions.",
    category: "operations",
    tags: ["application", "multi-step flow", "intake", "screening"],
    featured: false,
    integrations: ["Forms", "Multi-step flow", "AI agent", "Database", "Email"],
    prompt: `Please first produce a concise plan before building anything.

Build an application intake workflow that uses a multi-step flow (for example a grant, membership, loan, or program application). Split the application into a guided stepper: applicant details, eligibility questions, supporting documents, and review/consent.

Core steps:
1. Build the intake as a multi-step flow with one form as the flow entry and the remaining forms as flow members, advancing through a stepper.
2. Use conditional fields so applicants only see questions relevant to their answers.
3. Create an application row in a database table.
4. Use an AI agent to screen the application against the eligibility criteria and summarize strengths, gaps, and a recommended decision.
5. Use a gateway to route clearly ineligible applications to an auto-decline path and the rest to human review.
6. Create a dynamic review task for the reviewer to make the final decision.
7. Send a decision email (accepted, declined, or more information needed) and update the application status in the database.

Human review points: eligibility override, final decision, and requests for more information.

Outputs: application database record, AI screening summary, reviewer decision, and applicant notification.

Assumptions: if the eligibility criteria and steps are not defined, propose a default set of steps and rules and ask me to confirm before building.`,
  },
  {
    id: "artifact-approval",
    title: "Custom approval artifact",
    shortDescription:
      "Send a custom, shareable review UI for approvals a built-in form can't capture.",
    category: "operations",
    tags: ["artifact", "approval", "review", "shareable"],
    featured: false,
    integrations: ["Artifact", "AI agent", "Database", "Email"],
    prompt: `Please first produce a concise plan before building anything.

Build an approval workflow that uses a custom artifact instead of a standard form, for cases where the reviewer needs a richer, branded, shareable review surface (for example a side-by-side comparison, line-item approval, or a document mark-up view).

Core steps:
1. Start from a trigger form or webhook that submits the item to be reviewed, with all the data the reviewer needs to see.
2. Use an AI agent to prepare a clear summary and recommendation for the reviewer.
3. Store the request in a database table with a pending status.
4. Create an artifact: a custom, shareable review UI that presents the summary and the underlying details, with approve, reject, and request-changes actions.
5. Use a gateway to branch on the decision submitted through the artifact.
6. Update the database record with the decision and send a confirmation email to the requester.

Human review points: the artifact submission is the human decision point; add an escalation path for items left undecided past a deadline using a time delay.

Outputs: a shareable approval artifact, a decision record in the database, and a requester notification.

Assumptions: if the review content and exact decision actions are not specified, propose a default artifact layout and ask me to confirm before building.`,
  },
  {
    id: "quick-form-to-email",
    title: "Quick form to email",
    shortDescription:
      "A simple starter: capture a form, summarize it with AI, and send an email.",
    category: "it",
    tags: ["starter", "simple", "form", "email"],
    featured: false,
    integrations: ["Forms", "AI agent", "Email"],
    prompt: `Please first produce a concise plan before building anything.

Build a simple three-step workflow as a quick starting point. The workflow should start from a trigger form that captures a request (for example a question or a short submission) with a name, email, and message.

Core steps:
1. Use a trigger form to capture name, email, and the message.
2. Use an AI agent to summarize the submission and suggest a category and a next action.
3. Send an email to the team with the summary, and a confirmation email to the submitter.

Outputs: a confirmation email to the submitter and a summarized notification to the team.

Keep this workflow intentionally small and easy to extend later. Assumptions: if the recipients are not provided, ask me for the team email and use the form's email field for the submitter confirmation.`,
  },
  {
    id: "company-research-enrichment",
    title: "Company research and enrichment",
    shortDescription:
      "Research a company with AI web search, enrich a record, and store the profile.",
    category: "sales",
    tags: ["research", "enrichment", "web search", "account"],
    featured: false,
    integrations: ["Forms", "AI agent", "HTTP/API", "Database"],
    prompt: `Please first produce a concise plan before building anything.

Build a company research and enrichment workflow. The workflow should start from a trigger form, webhook, or a row added to a database table that provides a company name or domain.

Core steps:
1. Use an AI agent with web search enabled to research the company: industry, size, headquarters, products, recent news, and likely fit.
2. Optionally use an HTTP request to call an enrichment API for firmographic data if one is configured.
3. Use a code component to merge the AI findings and any API data into a single structured profile.
4. Store or update the enriched profile in a database table.
5. Use an AI agent to draft a short briefing with talking points and a recommended outreach angle.
6. Email the briefing to the account owner.

Human review points: an optional review task to validate the enriched profile before it is used for outreach.

Outputs: an enriched company profile in the database and an emailed research briefing.

Assumptions: if no enrichment API is available, rely on AI web search only. Ask me for the API details if you want one used.`,
  },
  {
    id: "data-cleanup-transform",
    title: "Data cleanup and transform",
    shortDescription:
      "Ingest a file or table, clean and transform it with code, and export the result.",
    category: "it",
    tags: ["data", "transform", "code", "etl"],
    featured: false,
    integrations: ["Forms", "Code", "Database", "File export"],
    prompt: `Please first produce a concise plan before building anything.

Build a data cleanup and transformation workflow. The workflow should start from a trigger form that uploads a CSV or XLSX file, or from a schedule trigger that reads rows from a database table.

Core steps:
1. Capture the input file via a trigger form, or query rows from a database table on a schedule.
2. Use a code component (Python with pandas) to clean the data: deduplicate, normalize formats, validate values, and flag rows with errors.
3. Use a gateway to separate clean rows from rows that need attention.
4. Optionally write the clean rows into a database table.
5. Use a create file step to export the cleaned dataset (CSV or XLSX) and a separate error report for the flagged rows.
6. Email the cleaned file and the error report to the requester.

Human review points: an optional review task for the flagged rows so a person can correct or approve them.

Outputs: a cleaned dataset file, an error report, and optionally updated database rows.

Assumptions: if the schema and cleaning rules are not defined, propose default rules (trim whitespace, standardize dates, validate emails) and ask me to confirm before building.`,
  },
  {
    id: "table-change-automation",
    title: "Database-triggered automation",
    shortDescription:
      "React to row changes in a table to notify, enrich, or kick off downstream work.",
    category: "operations",
    tags: ["database", "trigger", "automation", "no-code"],
    featured: false,
    integrations: ["Database", "AI agent", "Email"],
    prompt: `Please first produce a concise plan before building anything.

Build a data-driven workflow that runs automatically when a row changes in a database table. The workflow should start from a table trigger on insert, update, or delete.

Core steps:
1. Use a table trigger to start the workflow when a row is inserted or updated; it outputs the row id, event type, and new values.
2. Use a code or AI agent component to parse the changed row and decide what action is needed.
3. Use a gateway to branch on event type and on key field values (for example a status change to "ready" or "blocked").
4. For relevant changes, create a dynamic review task or send a notification email to the responsible owner.
5. Optionally update another row or table to keep related records in sync, using the row id from the trigger.

Human review points: an optional approval task for high-impact status changes before downstream updates are applied.

Outputs: notifications on relevant row changes, optional synced updates, and an audit trail of actions taken.

Assumptions: if the table, watched columns, and trigger conditions are not defined, ask me which table and which changes should start the workflow before building.`,
  },
  {
    id: "incident-escalation",
    title: "Incident escalation",
    shortDescription:
      "Open an incident, page the on-call, and escalate on a timer until acknowledged.",
    category: "it",
    tags: ["incident", "escalation", "on-call", "alerting"],
    featured: false,
    integrations: ["Webhook", "Email", "Database", "HTTP/API"],
    prompt: `Please first produce a concise plan before building anything.

Build an incident escalation workflow with a time-based escalation ladder. The workflow should start from a webhook trigger or trigger form that opens an incident with a title, severity, affected service, and description.

Core steps:
1. Use a webhook trigger or form to open the incident and capture severity and details.
2. Use an AI agent to summarize the incident and suggest a severity and first response.
3. Create an incident row in a database table with status "open" and the assigned on-call owner.
4. Notify the first on-call responder by email, and create a dynamic review task to acknowledge the incident.
5. Use an await-external-event or a time delay so the workflow waits for acknowledgement for a set period.
6. Use a gateway: if acknowledged in time, continue to resolution; if not, escalate to the next responder and repeat.
7. When resolved, update the incident record and send a resolution summary email.

Human review points: incident acknowledgement, severity override, and resolution confirmation.

Outputs: an incident record with a full timeline, escalation notifications, and a resolution summary.

Assumptions: if the escalation timing and responder ladder are not defined, propose defaults (for example 15 minutes per tier across three tiers) and ask me to confirm before building.`,
  },
];

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export function getWorkflowTemplateById(id) {
  const normalizedId = normalize(id);
  return (
    WORKFLOW_TEMPLATE_PROMPTS.find(
      (template) => normalize(template.id) === normalizedId
    ) || null
  );
}

export function getFeaturedWorkflowTemplatePrompts() {
  return WORKFLOW_TEMPLATE_PROMPTS.filter((template) => template.featured).map(
    ({ title, prompt }) => ({ title, prompt })
  );
}

export function searchWorkflowTemplates(query = "", category = "all") {
  const normalizedQuery = normalize(query);
  const normalizedCategory = normalize(category);

  return WORKFLOW_TEMPLATE_PROMPTS.filter((template) => {
    const categoryMatches =
      !normalizedCategory ||
      normalizedCategory === "all" ||
      normalize(template.category) === normalizedCategory;

    if (!categoryMatches) return false;
    if (!normalizedQuery) return true;

    const searchableParts = [
      template.title,
      template.shortDescription,
      template.category,
      ...(template.tags || []),
      ...(template.integrations || []),
    ];

    return searchableParts.some((part) =>
      normalize(part).includes(normalizedQuery)
    );
  });
}
